import React, { useEffect, useState } from 'react';
import { View, ScrollView, Alert, Text, Platform, Pressable } from 'react-native';
import { Button, ActivityIndicator, Portal, Modal } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchModelesBoitier,
  createModeleBoitier,
  updateModeleBoitier,
  deleteModeleBoitier,
} from '../redux/slices/modeleBoitierSlice';
import { fetchProduits } from '../redux/slices/produitsSlice';
import { produitServiceService } from '../services/produitService';
import EmptyState from '../components/EmptyState';
import {
  Badge, Card, IconCircleButton, PageHeader, Screen, SectionHeader, TextInput,
} from '../components/ui';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { ModeleBoitier, ProduitService, getServiceLabel } from '../types';

function confirmDelete(message: string, onConfirm: () => void) {
  if (Platform.OS === 'web') {
    const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
    const confirmed = typeof webGlobal.confirm === 'function' ? webGlobal.confirm(message) : true;
    if (confirmed) onConfirm();
    return;
  }
  Alert.alert('Supprimer', message, [
    { text: 'Annuler', style: 'cancel' },
    { text: 'Supprimer', style: 'destructive', onPress: onConfirm },
  ]);
}

type SettingRowProps = {
  title: string;
  value?: string;
  leading?: React.ReactNode;
  first?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  editLabel: string;
  deleteLabel: string;
};

/** Airbnb account-settings style row: label + value, "Modifier" link and a delete action. */
function SettingRow({ title, value, leading, first, onEdit, onDelete, editLabel, deleteLabel }: SettingRowProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();

  return (
    <View style={[styles.settingRow, !first && styles.rowDivider]}>
      {leading}
      <View style={styles.settingTexts}>
        <Text style={styles.settingTitle} numberOfLines={1}>{title}</Text>
        {value ? <Text style={styles.settingValue} numberOfLines={1}>{value}</Text> : null}
      </View>
      <Pressable
        onPress={onEdit}
        accessibilityRole="button"
        accessibilityLabel={editLabel}
        hitSlop={4}
        style={(state) => {
          const hovered = (state as { hovered?: boolean }).hovered || state.pressed;
          return [styles.editLink, hovered ? styles.editLinkHovered : null];
        }}
      >
        <Text style={styles.editLinkText}>Modifier</Text>
      </Pressable>
      <IconCircleButton
        icon="trash-can-outline"
        variant="ghost"
        color={colors.danger}
        onPress={onDelete}
        accessibilityLabel={deleteLabel}
      />
    </View>
  );
}

export default function Parametres() {
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((s) => s.modelesBoitier);
  const { list: produits, loading: produitsLoading } = useAppSelector((s) => s.produits);
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isWide } = useLayout();

  const [showModal, setShowModal] = useState(false);
  const [nomInput, setNomInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState<ModeleBoitier | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editNom, setEditNom] = useState('');

  const [showProduitModal, setShowProduitModal] = useState(false);
  const [editingProduit, setEditingProduit] = useState<ProduitService | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [nomProduit, setNomProduit] = useState('');
  const [prixInput, setPrixInput] = useState('');
  const [detailInput, setDetailInput] = useState('');
  const [produitError, setProduitError] = useState<string | null>(null);
  const [savingProduit, setSavingProduit] = useState(false);

  useEffect(() => {
    dispatch(fetchModelesBoitier());
    dispatch(fetchProduits());
  }, [dispatch]);

  const handleCreate = async () => {
    const nom = nomInput.trim();
    if (!nom) return;
    setSaving(true);
    try {
      await dispatch(createModeleBoitier(nom)).unwrap();
      setNomInput('');
      setShowModal(false);
    } catch {
      Alert.alert('Erreur', 'Ce modèle existe déjà ou une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item: ModeleBoitier) => {
    setEditingItem(item);
    setEditNom(item.nom);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingItem || !editNom.trim()) return;
    setSaving(true);
    try {
      await dispatch(updateModeleBoitier({ id: editingItem.id, nom: editNom.trim() })).unwrap();
      setShowEditModal(false);
    } catch {
      Alert.alert('Erreur', 'Ce nom existe déjà ou une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: ModeleBoitier) => {
    confirmDelete(`Supprimer le modèle "${item.nom}" ?`, () => {
      dispatch(deleteModeleBoitier(item.id));
    });
  };

  const openProduitCreate = () => {
    setEditingProduit(null);
    setCodeInput('');
    setNomProduit('');
    setPrixInput('');
    setDetailInput('');
    setProduitError(null);
    setShowProduitModal(true);
  };

  const openProduitEdit = (item: ProduitService) => {
    setEditingProduit(item);
    setCodeInput(item.code);
    setNomProduit(item.nom ?? '');
    setPrixInput(String(item.prixUnitaire));
    setDetailInput(item.detail ?? '');
    setProduitError(null);
    setShowProduitModal(true);
  };

  const handleSaveProduit = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code.startsWith('*') || code.length < 2) {
      setProduitError('Le code doit commencer par * (ex: *GPS).');
      return;
    }
    const prix = parseFloat(prixInput.replace(',', '.'));
    if (!Number.isFinite(prix) || prix <= 0) {
      setProduitError('Le prix unitaire doit être supérieur à 0.');
      return;
    }
    setProduitError(null);
    setSavingProduit(true);
    const payload = {
      code,
      nom: nomProduit.trim() || undefined,
      prixUnitaire: prix,
      detail: detailInput.trim() || undefined,
    };
    try {
      if (editingProduit) {
        await produitServiceService.update(editingProduit.id, payload);
      } else {
        await produitServiceService.create(payload);
      }
      await dispatch(fetchProduits());
      setShowProduitModal(false);
    } catch (e: any) {
      setProduitError(e?.response?.data?.erreur ?? 'Impossible d\'enregistrer ce produit.');
    } finally {
      setSavingProduit(false);
    }
  };

  const handleDeleteProduit = (item: ProduitService) => {
    confirmDelete(`Supprimer ${item.code} du catalogue ?`, async () => {
      try {
        await produitServiceService.delete(item.id);
        dispatch(fetchProduits());
      } catch (e: any) {
        const message = e?.response?.data?.erreur ?? 'Impossible de supprimer ce produit.';
        if (Platform.OS === 'web') {
          const webGlobal = globalThis as unknown as { alert?: (msg: string) => void };
          webGlobal.alert?.(message);
          return;
        }
        Alert.alert('Erreur', message);
      }
    });
  };

  const produitsTries = [...produits].sort((a, b) => a.code.localeCompare(b.code));
  const plural = (n: number, singular: string, pluralForm: string) => `${n} ${n > 1 ? pluralForm : singular}`;

  return (
    <Screen>
      <PageHeader
        title="Paramètres"
        subtitle="Modèles de boîtiers GPS et catalogue des produits et services."
      />

      <View style={[styles.sections, isWide && styles.sectionsWide]}>
        {/* Modèles de boitier */}
        <View style={[styles.section, isWide && styles.sectionNarrow]}>
          <SectionHeader
            title="Modèles de boîtier GPS"
            subtitle={loading ? undefined : plural(list.length, 'modèle', 'modèles')}
            right={(
              <Button
                mode="outlined"
                icon="plus"
                compact
                textColor={colors.textPrimary}
                onPress={() => { setNomInput(''); setShowModal(true); }}
              >
                Ajouter
              </Button>
            )}
          />
          <Card flush>
            {loading ? (
              <ActivityIndicator color={colors.primary} style={styles.loader} />
            ) : list.length === 0 ? (
              <EmptyState icon="crosshairs-gps" message="Aucun modèle défini" />
            ) : (
              list.map((item, index) => (
                <SettingRow
                  key={item.id}
                  first={index === 0}
                  title={item.nom}
                  leading={(
                    <View style={styles.leadingIcon}>
                      <MaterialCommunityIcons name="crosshairs-gps" size={18} color={colors.textPrimary} />
                    </View>
                  )}
                  onEdit={() => openEdit(item)}
                  onDelete={() => handleDelete(item)}
                  editLabel={`Modifier le modèle ${item.nom}`}
                  deleteLabel={`Supprimer le modèle ${item.nom}`}
                />
              ))
            )}
          </Card>
        </View>

        {/* Catalogue produits / services */}
        <View style={[styles.section, isWide && styles.sectionWide]}>
          <SectionHeader
            title="Catalogue produits / services"
            subtitle={produitsLoading ? undefined : plural(produitsTries.length, 'produit', 'produits')}
            right={(
              <Button
                mode="outlined"
                icon="plus"
                compact
                textColor={colors.textPrimary}
                onPress={openProduitCreate}
              >
                Ajouter
              </Button>
            )}
          />
          <Card flush>
            {produitsLoading ? (
              <ActivityIndicator color={colors.primary} style={styles.loader} />
            ) : produitsTries.length === 0 ? (
              <EmptyState icon="package-variant-closed" message="Aucun produit dans le catalogue" />
            ) : (
              produitsTries.map((item, index) => (
                <SettingRow
                  key={item.id}
                  first={index === 0}
                  title={item.nom || getServiceLabel(item.code)}
                  value={`${Number(item.prixUnitaire).toLocaleString('fr-FR')} DH`}
                  leading={<Badge label={item.code} tone="brand" style={styles.codeBadge} />}
                  onEdit={() => openProduitEdit(item)}
                  onDelete={() => handleDeleteProduit(item)}
                  editLabel={`Modifier ${item.code}`}
                  deleteLabel={`Supprimer ${item.code}`}
                />
              ))
            )}
          </Card>
        </View>
      </View>

      <Portal>
        <Modal
          visible={showModal}
          onDismiss={() => setShowModal(false)}
          contentContainerStyle={styles.modal}
        >
          <Text style={styles.modalTitle}>Nouveau modèle de boîtier</Text>
          <TextInput
            label="Nom du modèle"
            value={nomInput}
            onChangeText={setNomInput}
            style={styles.input}
            autoFocus
          />
          <View style={styles.actions}>
            <Button textColor={colors.textPrimary} onPress={() => setShowModal(false)}>Annuler</Button>
            <Button
              mode="contained"
              onPress={handleCreate}
              loading={saving}
              disabled={saving || !nomInput.trim()}
            >
              Enregistrer
            </Button>
          </View>
        </Modal>

        <Modal
          visible={showEditModal}
          onDismiss={() => setShowEditModal(false)}
          contentContainerStyle={styles.modal}
        >
          <Text style={styles.modalTitle}>Modifier le modèle</Text>
          <TextInput
            label="Nouveau nom"
            value={editNom}
            onChangeText={setEditNom}
            style={styles.input}
            autoFocus
          />
          <View style={styles.actions}>
            <Button textColor={colors.textPrimary} onPress={() => setShowEditModal(false)}>Annuler</Button>
            <Button
              mode="contained"
              onPress={handleUpdate}
              loading={saving}
              disabled={saving || !editNom.trim() || editNom.trim() === editingItem?.nom}
            >
              Modifier
            </Button>
          </View>
        </Modal>

        <Modal
          visible={showProduitModal}
          onDismiss={() => setShowProduitModal(false)}
          contentContainerStyle={[styles.modal, styles.modalScrollable]}
        >
          <ScrollView keyboardShouldPersistTaps="handled">
            <Text style={styles.modalTitle}>
              {editingProduit ? 'Modifier le produit' : 'Nouveau produit / service'}
            </Text>
            <TextInput
              label="Code * (ex: *GPS)"
              value={codeInput}
              onChangeText={(value) => { setProduitError(null); setCodeInput(value.toUpperCase()); }}
              style={styles.input}
              autoCapitalize="characters"
              autoFocus
            />
            <TextInput
              label="Nom du service"
              value={nomProduit}
              onChangeText={setNomProduit}
              style={styles.input}
            />
            <TextInput
              label="Prix unitaire (DH) *"
              value={prixInput}
              onChangeText={(value) => { setProduitError(null); setPrixInput(value); }}
              keyboardType="decimal-pad"
              style={styles.input}
            />
            <TextInput
              label="Détail"
              value={detailInput}
              onChangeText={setDetailInput}
              style={styles.input}
              multiline
            />
            {produitError ? (
              <View style={styles.errorRow}>
                <MaterialCommunityIcons name="alert-circle-outline" size={16} color={colors.danger} />
                <Text style={styles.error}>{produitError}</Text>
              </View>
            ) : null}
            <View style={styles.actions}>
              <Button textColor={colors.textPrimary} onPress={() => setShowProduitModal(false)}>Annuler</Button>
              <Button
                mode="contained"
                onPress={handleSaveProduit}
                loading={savingProduit}
                disabled={savingProduit || !codeInput.trim() || !prixInput.trim()}
              >
                Enregistrer
              </Button>
            </View>
          </ScrollView>
        </Modal>
      </Portal>
    </Screen>
  );
}

const useStyles = makeStyles((c) => ({
  sections: { gap: 32 },
  sectionsWide: { flexDirection: 'row', alignItems: 'flex-start', gap: 32 },
  section: { minWidth: 0 },
  sectionNarrow: { flex: 2 },
  sectionWide: { flex: 3 },
  loader: { marginVertical: 32 },

  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 64,
    paddingVertical: 12,
    paddingLeft: 20,
    paddingRight: 12,
  },
  rowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  leadingIcon: {
    width: 36,
    height: 36,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  codeBadge: { alignSelf: 'center' },
  settingTexts: { flex: 1, minWidth: 0 },
  settingTitle: { fontSize: 15, fontWeight: '600', color: c.textPrimary },
  settingValue: { fontSize: 14, color: c.textSecondary, marginTop: 2 },
  editLink: {
    minHeight: 40,
    justifyContent: 'center',
    paddingHorizontal: 8,
    borderRadius: RADIUS.sm,
  },
  editLinkHovered: { backgroundColor: c.surfaceAlt },
  editLinkText: { ...TYPOGRAPHY.label, fontSize: 14, color: c.textPrimary, textDecorationLine: 'underline' },

  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    margin: 16,
    maxWidth: 560,
    width: '100%',
    alignSelf: 'center',
  },
  modalScrollable: { maxHeight: '90%' },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary, marginBottom: 20 },
  input: { marginBottom: 12 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  errorRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 },
  error: { color: c.danger, fontSize: 13, flexShrink: 1 },
}));
